import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ShoppingBagIcon, Trash2Icon, XIcon } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { formatPrice } from '../../utils/currency';
import { FREE_SHIPPING_THRESHOLD } from '../../utils/shipping';
import { QuantityStepper } from '../shop/QuantityStepper';
const ease = [0.23, 1, 0.32, 1] as const;
export function CartDrawer() {
  const {
    isOpen,
    close,
    lines,
    subtotal,
    setQty,
    remove,
    count
  } = useCart();
  const navigate = useNavigate();
  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);
  const progress = Math.min(1, subtotal / FREE_SHIPPING_THRESHOLD);
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, close]);
  return <AnimatePresence data-id="element-129" data-mp="ci5rsz12ajv2">
      {isOpen && <div className="fixed inset-0 z-50" data-id="element-130" data-mp="cj72h2e614c9">
          <motion.div className="absolute inset-0 bg-ink/40" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={close} data-id="element-131" data-mp="hhib59sq58sw" />
          <motion.aside role="dialog" aria-modal="true" aria-label="Winkelwagen" className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col bg-canvas" initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        duration: 0.28,
        ease
      }} data-id="element-132" data-mp="7hrkjj7abdy3">
            <div className="flex h-16 items-center justify-between border-b border-line px-6" data-id="element-133" data-mp="3kz8xteo6jv75">
              <h2 className="text-lg font-semibold tracking-tight" data-id="element-134" data-mp="36zr6osnew5um">Winkelwagen {count > 0 && <span className="text-muted" data-id="element-135" data-mp="3byapwlamf3ja">({count})</span>}</h2>
              <button onClick={close} className="grid h-10 w-10 place-items-center rounded-full hover:bg-surface" aria-label="Sluiten" data-id="element-136" data-mp="3bzlvmqnmmld1">
                <XIcon className="h-5 w-5" data-id="element-137" data-mp="1cqlxrlpgoy2q" />
              </button>
            </div>

            {lines.length === 0 ? <div className="flex flex-1 flex-col items-center justify-center px-8 text-center" data-id="element-138" data-mp="17skht9nc1evh">
                <span className="grid h-14 w-14 place-items-center rounded-full bg-surface" data-id="element-139" data-mp="13rgygd62t0pi">
                  <ShoppingBagIcon className="h-6 w-6 text-muted" data-id="element-140" data-mp="20f56g2cfbcvc" />
                </span>
                <p className="mt-5 text-lg font-semibold" data-id="element-141" data-mp="1vierxjg9opc1">Je winkelwagen is leeg</p>
                <p className="mt-1 text-sm text-muted" data-id="element-142" data-mp="1rf8m2cx2h063">Begin met onze bestseller: het complete hammam-ritueel.</p>
                <Link to="/product/hammam-ritueel-set" onClick={close} className="mt-6 inline-flex h-12 items-center rounded-full bg-ink px-6 text-sm font-medium text-canvas hover:bg-clay" data-id="element-143" data-mp="1mflz3brfs5k3">
                  Bekijk de Hammam Ritueel Set
                </Link>
              </div> : <>
                <div className="border-b border-line px-6 py-4" data-id="element-144" data-mp="2rnf0h7krwz5y">
                  <p className="text-sm" data-id="element-145" data-mp="2rlkf5s5lb51c">
                    {remaining > 0 ? <>Nog <strong data-id="element-146" data-mp="2vnfif272zshr">{formatPrice(remaining)}</strong> tot gratis verzending</> : <strong className="text-olive" data-id="element-147" data-mp="35fbtddu9nd3e">Je bestelling wordt gratis verzonden</strong>}
                  </p>
                  <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-line" data-id="element-148" data-mp="3addb9vfyh6da">
                    <div className={`h-full rounded-full transition-[width] duration-300 ease-out ${remaining > 0 ? 'bg-clay' : 'bg-olive'}`} style={{
                width: `${progress * 100}%`
              }} data-id="element-149" data-mp="3fd9f9uonkxsu" />
                  </div>
                </div>

                <ul className="flex-1 divide-y divide-line overflow-y-auto px-6" data-id="element-150" data-mp="1p0jzsxldvzsi">
                  {lines.map(l => <li key={`${l.slug}-${l.variantId}`} className="flex gap-4 py-5" data-id="element-151" data-mp="tpvhjktvt5cw">
                      <Link to={`/product/${l.slug}`} onClick={close} className="shrink-0" data-id="element-152" data-mp="pq37t4sr4t5y">
                        <img src={l.product.image} alt="" className="h-20 w-20 rounded-xl bg-surface object-cover" data-id="element-153" data-mp="13pcpk8wlvvc1" />
                      </Link>
                      <div className="flex flex-1 flex-col" data-id="element-154" data-mp="yqsyxv72hvdc">
                        <div className="flex justify-between gap-3" data-id="element-155" data-mp="b1gv7auiefif">
                          <div data-id="element-156" data-mp="61m3x66tnnwu">
                            <p className="font-medium" data-id="element-157" data-mp="62dnfin59v6r">{l.product.name}</p>
                            <p className="text-sm text-muted" data-id="element-158" data-mp="kqqmwplfxw60">{l.variant.label}</p>
                          </div>
                          <p className="font-medium tabular-nums" data-id="element-159" data-mp="fz8jluvy3jhv">{formatPrice(l.total)}</p>
                        </div>
                        <div className="mt-auto flex items-center justify-between pt-3" data-id="element-160" data-mp="3oe0nw1xiy2fm">
                          <QuantityStepper value={l.qty} onChange={q => setQty(l.slug, l.variantId, q)} data-id="element-161" data-mp="3jf79866i45dv" />
                          <button onClick={() => remove(l.slug, l.variantId)} className="grid h-9 w-9 place-items-center rounded-full text-muted hover:bg-surface hover:text-ink" aria-label={`${l.product.name} verwijderen`} data-id="element-162" data-mp="1yxepfj2yt9ob">
                            <Trash2Icon className="h-4 w-4" data-id="element-163" data-mp="1twqw2qr9kgqx" />
                          </button>
                        </div>
                      </div>
                    </li>)}
                </ul>

                <div className="border-t border-line px-6 py-5" data-id="element-164" data-mp="28o9kncy6vjlw">
                  <div className="flex items-baseline justify-between" data-id="element-165" data-mp="28q3vtzjnrxcs">
                    <span className="text-muted" data-id="element-166" data-mp="2dlrjgaihsig6">Subtotaal</span>
                    <span className="text-xl font-semibold tabular-nums" data-id="element-167" data-mp="x8b6wvs2g9ec">{formatPrice(subtotal)}</span>
                  </div>
                  <p className="mt-1 text-xs text-muted" data-id="element-168" data-mp="128yt945co228">Inclusief btw. Verzendkosten bij het afrekenen.</p>
                  <button onClick={() => {
              close();
              navigate('/afrekenen');
            }} className="mt-5 flex h-14 w-full items-center justify-center rounded-full bg-ink text-base font-medium text-canvas transition-colors duration-150 hover:bg-clay" data-id="element-169" data-mp="33vs275azk7jm">
                    Afrekenen
                  </button>
                  <p className="mt-3 text-center text-xs text-muted" data-id="element-170" data-mp="2l7wtw0y57lxw">iDEAL · Creditcard · Klarna · PayPal</p>
                </div>
              </>}
          </motion.aside>
        </div>}
    </AnimatePresence>;
}